import React, { useCallback, useEffect, useState } from "react";
import { ChevronRight, Download } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import useVendorStore from "../../HomePage/useVendorStore";
import { Loader } from "../../Common/Loader";
import ShowToast from "../../Common/Toast";
import { STATUS_BEGIN, STATUS_FAIL } from "../../../../utils/constants/others";
import { getPurchaseOrderEmptyStateDestination, getPurchaseOrderPageConfig, PURCHASE_ORDER_PAGE_CONFIG, PURCHASE_ORDER_PAGES, PURCHASE_ORDER_STATUSES, PO_STATUS_VALUES } from "./config";
import { PurchaseOrderCard, PurchaseOrderContactModal, PurchaseOrderEmptyState, PurchaseOrderErrorState, PurchaseOrderListHeader, PurchaseOrderSearchPrompt, PurchaseOrderStatusFilters } from "./PurchaseOrderListElements";
import { CardList, Content, Page, ScrollLoader, StateContainer } from "./styles";

const PURCHASE_ORDER_LIST_SCROLL_CONTAINER_ID =
  "purchase-order-list-scroll-container";
const MIN_PURCHASE_ORDER_SEARCH_LENGTH = 3;
const PURCHASE_ORDER_ID_LENGTH = 7;

const getMinimumSearchLength = (searchTerm) =>
  /^\d+$/.test(searchTerm)
    ? PURCHASE_ORDER_ID_LENGTH
    : MIN_PURCHASE_ORDER_SEARCH_LENGTH;

const useDebouncedValue = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timeout);
  }, [delay, value]);

  return debouncedValue;
};

const PurchaseOrderListView = () => {
  const navigate = useNavigate();
  const outletId = window.localStorage.getItem("outlet_id");
  const {
    getPurchaseOrderDetails,
    getPurchaseOrderPageCounts,
    resetPurchaseOrderDetails,
    getPurchaseOrderDetailsStatus,
    purchaseOrderDetails,
    hasNextPurchaseOrderDetailsPage,
  } = useVendorStore();

  const [activePage, setActivePage] = useState(PURCHASE_ORDER_PAGES.NEW);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCounts, setPageCounts] = useState({});
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const normalizedSearchQuery = searchQuery.trim();
  const debouncedSearchQuery = useDebouncedValue(normalizedSearchQuery, 400);
  const minimumSearchLength = getMinimumSearchLength(normalizedSearchQuery);
  const debouncedMinimumSearchLength = getMinimumSearchLength(
    debouncedSearchQuery
  );
  const isSearchReady =
    debouncedSearchQuery.length >= debouncedMinimumSearchLength;
  const isSearching = searchVisible && isSearchReady;
  const purchaseOrderSearchQuery = isSearching ? debouncedSearchQuery : "";
  const showSearchPrompt =
    searchVisible &&
    (normalizedSearchQuery.length < minimumSearchLength || !isSearchReady);

  const activePageConfig = getPurchaseOrderPageConfig(activePage);
  const emptyStateDestination = getPurchaseOrderEmptyStateDestination(
    activePage,
    pageCounts
  );

  const fetchPurchaseOrders = useCallback(
    async (requestedPageNumber = 1, isFirstPage = true) => {
      let statuses = isSearching
        ? []
        : selectedStatus
          ? [selectedStatus]
          : activePageConfig.statuses;

      // For CLOSED page, default to only EXPIRED if no filter is selected
      if (activePage === PURCHASE_ORDER_PAGES.CLOSED && !selectedStatus && !isSearching) {
        statuses = [PURCHASE_ORDER_STATUSES.EXPIRED];
      }

      const listResponse = await getPurchaseOrderDetails(
        {
          statuses,
          po_status: isSearching
            ? "APPROVED,COMPLETED,WAITING_FOR_VENDOR_APPROVAL,PENDING_ADMIN_APPROVAL"
            : "APPROVED,COMPLETED,PENDING_ADMIN_APPROVAL",
          vendor_id: [outletId],
          purchase_order_number: purchaseOrderSearchQuery,
          page_number: requestedPageNumber,
          page_no: requestedPageNumber,
        },
        isFirstPage
      );

      if (listResponse && !selectedStatus && !isSearching) {
        setPageCounts((counts) => ({
          ...counts,
          [activePage]:
            Number(listResponse.total_count ?? listResponse.order_count) || 0,
        }));
      }

      return listResponse;
    },
    [
      activePage,
      activePageConfig.statuses,
      getPurchaseOrderDetails,
      isSearching,
      outletId,
      purchaseOrderSearchQuery,
      selectedStatus,
    ]
  );

  useEffect(() => {
    setPageNumber(1);
    if (searchVisible && !isSearching) {
      resetPurchaseOrderDetails();
      return;
    }
    fetchPurchaseOrders(1, true);
  }, [
    fetchPurchaseOrders,
    isSearching,
    resetPurchaseOrderDetails,
    searchVisible,
  ]);

  useEffect(() => {
    let isMounted = true;
    const pageConfigsToPreload = PURCHASE_ORDER_PAGE_CONFIG.filter(
      (page) => page.key !== PURCHASE_ORDER_PAGES.NEW
    );

    getPurchaseOrderPageCounts(pageConfigsToPreload, [outletId])
      .then((counts) => {
        if (isMounted) {
          setPageCounts((currentCounts) => ({
            ...counts,
            ...currentCounts,
          }));
        }
      });

    return () => {
      isMounted = false;
    };
  }, [getPurchaseOrderPageCounts, outletId]);

  useEffect(
    () => () => {
      resetPurchaseOrderDetails();
    },
    [resetPurchaseOrderDetails]
  );

  const handlePageChange = (page) => {
    if (page === activePage) {
      return;
    }
    setSelectedStatus("");
    setActivePage(page);
  };

  const loadNextPage = async () => {
    if (
      getPurchaseOrderDetailsStatus === STATUS_BEGIN ||
      !hasNextPurchaseOrderDetailsPage
    ) {
      return;
    }

    const nextPage = pageNumber + 1;
    const listResponse = await fetchPurchaseOrders(nextPage, false);
    if (listResponse) {
      setPageNumber(nextPage);
    }
  };

  const handleDownload = (po) => {
    setToastMessage("");
    const documentUrl = po.po_document_url?.trim();
    if (!documentUrl) {
      setToastMessage("PO document is not available");
      return;
    }

    try {
      if (window?.downloadFileMessageHandler) {
        window.downloadFileMessageHandler.postMessage(documentUrl);
        return;
      }

      const downloadLink = document.createElement("a");
      downloadLink.href = documentUrl;
      downloadLink.target = "_blank";
      downloadLink.rel = "noopener noreferrer";
      downloadLink.download = `PO-${po.purchase_order_number}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
    } catch {
      setToastMessage("Something went wrong downloading the PO document");
    }
  };

  const handleOpen = (po, status) => {
    const source =
      status === PO_STATUS_VALUES.WAITING_FOR_VENDOR_APPROVAL
        ? "list-pending"
        : "list";
    navigate(`/po/details/${source}/${po.id}`);
  };

  const handleContact = (po) => {
    setToastMessage("");
    const phoneNumber = String(po.created_by_phone_no || "").trim();
    if (!phoneNumber) {
      setToastMessage("Contact details are not available");
      return;
    }

    setSelectedContact({
      name: po.created_by_user_name || "PO creator",
      phoneNumber,
    });
  };

  const handleCall = () => {
    const formattedPhoneNumber = selectedContact?.phoneNumber.replace(/\s/g, "");
    if (formattedPhoneNumber) {
      window.location.href = `tel:+91${formattedPhoneNumber}`;
    }
  };

  const getPurchaseOrderActions = (item) => {
    const purchaseOrder = item;
    const documentUrl = purchaseOrder.po_document_url?.trim();
    return [
      documentUrl && {
        key: "download",
        label: `Download ${purchaseOrder.purchase_order_number}`,
        icon: <Download size={19} />,
        onClick: () => handleDownload(purchaseOrder),
      },
      {
        key: "open",
        label: `View ${purchaseOrder.purchase_order_number}`,
        icon: <ChevronRight size={20} />,
        onClick: () => handleOpen(purchaseOrder, purchaseOrder.po_status),
      },
    ].filter(Boolean);
  };

  const toggleSearch = () => {
    if (searchVisible) {
      setSearchQuery("");
    }
    setSearchVisible((visible) => !visible);
  };

  const isInitialLoading =
    getPurchaseOrderDetailsStatus === STATUS_BEGIN &&
    pageNumber === 1 &&
    purchaseOrderDetails.length === 0;
  const hasError =
    getPurchaseOrderDetailsStatus === STATUS_FAIL &&
    purchaseOrderDetails.length === 0;

  return (
    <Page id={PURCHASE_ORDER_LIST_SCROLL_CONTAINER_ID}>
      <PurchaseOrderListHeader
        activePage={activePage}
        searchQuery={searchQuery}
        searchVisible={searchVisible}
        onPageChange={handlePageChange}
        onSearchChange={setSearchQuery}
        onToggleSearch={toggleSearch}
      />

      <Content>
        {!searchVisible && (
          <PurchaseOrderStatusFilters
            statuses={activePageConfig.statuses}
            selectedStatus={selectedStatus}
            onChange={setSelectedStatus}
          />
        )}

        {showSearchPrompt && (
          <PurchaseOrderSearchPrompt
            minimumCharacters={minimumSearchLength}
            numericOnly={/^\d+$/.test(normalizedSearchQuery)}
          />
        )}

        {!showSearchPrompt && isInitialLoading && (
          <StateContainer>
            <Loader isScrollLoader />
          </StateContainer>
        )}

        {!showSearchPrompt && hasError && (
          <PurchaseOrderErrorState
            onRetry={() => fetchPurchaseOrders(1, true)}
          />
        )}

        {!showSearchPrompt &&
          !isInitialLoading &&
          !hasError &&
          purchaseOrderDetails.length === 0 && (
            <PurchaseOrderEmptyState
              pageConfig={activePageConfig}
              isSearch={isSearching}
              selectedStatus={selectedStatus}
              nextPage={emptyStateDestination}
              nextPageCount={emptyStateDestination ? pageCounts[emptyStateDestination.key] : 0}
              onOpenPage={handlePageChange}
            />
          )}

        {!showSearchPrompt && purchaseOrderDetails.length > 0 && (
          <InfiniteScroll
            dataLength={purchaseOrderDetails.length}
            hasMore={hasNextPurchaseOrderDetailsPage}
            next={loadNextPage}
            scrollableTarget={PURCHASE_ORDER_LIST_SCROLL_CONTAINER_ID}
            loader={
              <ScrollLoader>
                <Loader isScrollLoader />
              </ScrollLoader>
            }
          >
            <CardList>
              {purchaseOrderDetails.map((item) => (
                <PurchaseOrderCard
                  key={`${item.id}-${item.po_status}`}
                  item={item}
                  actions={getPurchaseOrderActions(item)}
                />
              ))}
            </CardList>
          </InfiniteScroll>
        )}
      </Content>

      <PurchaseOrderContactModal
        contact={selectedContact}
        onClose={() => setSelectedContact(null)}
        onCall={handleCall}
      />

      {toastMessage && <ShowToast errorMsg={toastMessage} />}
    </Page>
  );
};

export default PurchaseOrderListView;
